import { useEffect, useState } from 'react';
import type { PublicFrame } from '@mantelpiece/shared';
import { api } from '../lib/api';
import { Shell } from '../components/Shell';
import { UploadPanel } from '../components/UploadPanel';

const NAME_KEY = 'mantelpiece.public.name';

interface Props {
  readonly slug: string;
}

/**
 * The no-account door: someone was sent a link and wants to put a photo on the
 * telly. All it asks for is a name, because a photo on a frame with nobody's
 * name under it is worse than no photo.
 */
export const PublicUpload = ({ slug }: Props) => {
  const [frame, setFrame] = useState<PublicFrame | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState(() => localStorage.getItem(NAME_KEY) ?? '');

  useEffect(() => {
    let live = true;
    api
      .publicFrame(slug)
      .then((result) => live && setFrame(result))
      .catch((caught: unknown) => {
        if (!live) return;
        setError(caught instanceof Error ? caught.message : 'That link does not lead anywhere.');
      });
    return () => {
      live = false;
    };
  }, [slug]);

  const remember = (value: string) => {
    setName(value);
    // Saves typing it again next time. It is a display name, not a credential.
    try {
      localStorage.setItem(NAME_KEY, value);
    } catch {
      /* Private browsing. Not worth failing over. */
    }
  };

  if (error) {
    return (
      <Shell>
        <p className="alert" style={{ marginTop: '2.5rem' }}>
          {error}
        </p>
      </Shell>
    );
  }

  if (!frame) {
    return (
      <Shell>
        <p className="eyebrow" style={{ marginTop: '2.5rem' }}>
          Loading
        </p>
      </Shell>
    );
  }

  if (!frame.open) {
    return (
      <Shell>
        <div style={{ marginTop: '2.5rem' }}>
          <p className="eyebrow">{frame.deviceLabel}</p>
          <h1 className="title">This link is not taking photos right now</h1>
          <p className="lede">
            {frame.ownerName} has turned off sending by link. Ask them to switch it back on, or for
            an invite of your own.
          </p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="stack-loose" style={{ marginTop: '2.5rem' }}>
        <div>
          <p className="eyebrow">{frame.ownerName}'s frame</p>
          <h1 className="title">Put a photo on {frame.deviceLabel}</h1>
          <p className="lede">
            No account needed. Photos are resized on this device before sending, so it works fine
            on mobile data.
          </p>
        </div>

        <div className="stack">
          <label className="field">
            <span>Your name</span>
            <input
              className="input"
              value={name}
              maxLength={40}
              autoComplete="name"
              placeholder="So everyone knows who sent it"
              onChange={(event) => remember(event.target.value)}
            />
          </label>

          {!frame.autoAccept && (
            <p className="alert" data-tone="ok">
              {frame.ownerName} checks photos sent through this link before they go on the TV.
            </p>
          )}
        </div>

        {name.trim().length < 2 ? (
          <p className="empty">Add your name above and the picker will appear.</p>
        ) : (
          <UploadPanel
            upload={(photo, text) => api.publicUpload(slug, name.trim(), photo, text)}
          />
        )}
      </div>
    </Shell>
  );
};
